// Browser-side Claude wrappers. All calls route through /api/ai (server-side)
// so ANTHROPIC_API_KEY never reaches the browser. Mirrors Camilla's src/ai.js
// shape so the rest of the app's call-sites stay identical.

import { RMW, STORES } from './constants.js';

async function callAI(system, user, maxTokens = 1200) {
  if (!system || !user) throw new Error('callAI requires system and user prompts');

  const res = await fetch('/api/ai', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ system, user, maxTokens }),
  });
  if (res.status === 401) throw new Error('Session expired — please log in again');
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(err.error || `AI request failed: ${res.status}`);
  }
  return res.json();
}

const brandCtx = `
BRAND: ${RMW.name} — ${RMW.positioning}
FOUNDED: ${RMW.founded} by ${RMW.founder} in ${RMW.hq}
BRAND VOICE: ${RMW.voice}

KEY BRAND VALUES:
${RMW.usps.map(u => `• ${u.k}: ${u.d}`).join('\n')}

KEY MESSAGES:
${RMW.keyMessages.map(m => `• ${m}`).join('\n')}
`;

const GLOBAL_RULES = `
ABSOLUTE RULES:
- Write in R.M.Williams' voice: plainspoken, confident, hard-wearing. Australian without affectation.
- Lead with craft, materials, construction — never with a "lifestyle".
- Reference yearling leather, whole-cut construction, Goodyear welts where accurate.
- Boots are re-soleable for life — say so when relevant.
- Heritage matters: Adelaide, 1932, family-founded. Don't oversell it.
- Use Australian English spelling (colour, favourite, jewellery, organisation).
- Never invent specific leather grades, fabric percentages, or care instructions you don't know.
- Respond ONLY with valid JSON — no preamble, no markdown fences.

AI-DETECTION AVOIDANCE (CRITICAL — output goes to a real catalogue):
- NEVER use em-dash (—) as separator in bullets.
- No "Feature — benefit" pattern. Plain fragments instead.
- Vary sentence length and structure naturally.
- No filler: "whether you're", "designed to", "perfect for", "making it", "ensuring that".
- No "elevate your", "step into", "embrace", "discover the", "introducing", "meet the".
- One adjective per noun max. Concrete details over vague praise.
- Match the register of existing rmwilliams.com.au pages — confident, dry, declarative.
- Never use: "stunning", "beautiful", "luxurious", "premium" (overused), "flattering".
`;

function getStoreCtx(storeId) {
  const store = STORES.find(s => s.id === storeId);
  if (!store) return '';
  return `STORE: ${store.label} (${store.domain}) — currency ${store.currency}`;
}

export async function rewriteDescription(product) {
  const system = `You are R.M.Williams' senior copywriter. You write product descriptions for boots, leather goods, and apparel.
${brandCtx}${GLOBAL_RULES}
Return JSON: { "description": "...", "shortDescription": "...(1 sentence, 120 chars max)", "wordCount": 0, "keyHighlights": ["..."], "tone": "..." }`;

  const user = `Write a product description for:
Title: ${product.title}
Type: ${product.productType || product._ai_dept || 'unknown'}
Vendor: ${product.vendor || 'R.M.Williams'}
Tags: ${(product.tags || []).slice(0, 20).join(', ')}
Current description: ${product.description || '(none)'}
Sizes: ${(product.sizes || []).join(', ') || 'unknown'}
Widths: ${(product.widths || []).join(', ') || 'unknown'}
Colours: ${(product.colours || []).join(', ') || 'unknown'}
${product.storeId ? getStoreCtx(product.storeId) : ''}

Write 100-150 words. Lead with construction or material. Close on durability or fit.`;

  return callAI(system, user);
}

export async function rewriteTitle(product) {
  const system = `You are R.M.Williams' SEO and copywriting specialist.
${brandCtx}${GLOBAL_RULES}
Return JSON: { "title": "...", "titleLength": 0, "reasoning": "..." }`;

  const user = `Write an SEO-optimised page title for:
Title: ${product.title}
Type: ${product.productType || product._ai_dept || 'unknown'}
Tags: ${(product.tags || []).slice(0, 10).join(', ')}

Rules: 50-65 characters, include "R.M.Williams", include the key product type, natural — not keyword-stuffed.`;

  return callAI(system, user, 400);
}

export async function rewriteMeta(product) {
  const system = `You are R.M.Williams' SEO specialist.
${brandCtx}${GLOBAL_RULES}
Return JSON: { "metaDescription": "...", "metaLength": 0, "primaryKeywords": ["..."] }`;

  const user = `Write an SEO meta description for:
Title: ${product.title}
Type: ${product.productType || product._ai_dept || 'unknown'}
Description: ${(product.description || '').slice(0, 300)}
Tags: ${(product.tags || []).slice(0, 10).join(', ')}

Rules: 140-160 characters, include product type and brand name, declarative, action-oriented, Australian English.`;

  return callAI(system, user, 400);
}

export async function enrichProduct(product) {
  const system = `You are R.M.Williams' product data specialist. You enrich product records with tags and metadata for the catalogue.
${brandCtx}${GLOBAL_RULES}
Return JSON: { "tags": ["..."], "leatherType": "...", "constructionNotes": "...", "fitNotes": "...", "occasionTags": ["..."], "colourFamily": "...", "careInstructions": "..." }`;

  const user = `Enrich this R.M.Williams product:
Title: ${product.title}
Type: ${product.productType || product._ai_dept || 'unknown'}
Current tags: ${(product.tags || []).join(', ') || '(none)'}
Description: ${(product.description || '').slice(0, 500)}
Sizes: ${(product.sizes || []).join(', ') || 'unknown'}
Widths: ${(product.widths || []).join(', ') || 'unknown'}
Colours: ${(product.colours || []).join(', ') || 'unknown'}

Generate 8-12 tags covering: product type, construction (welted/whole-cut/etc), occasion (work/dress/weekend),
leather type (yearling/kangaroo/suede), colour family, fit, season.`;

  return callAI(system, user, 1000);
}

export async function compareAcrossStores(productsByStore) {
  const system = `You are R.M.Williams' global merchandising analyst.
${GLOBAL_RULES}
Return JSON: { "summary": "...", "storeInsights": { ... }, "contentGaps": ["..."], "recommendations": ["..."] }`;

  const storeData = Object.entries(productsByStore).map(([storeId, products]) => {
    const store = STORES.find(s => s.id === storeId);
    return `${store?.label || storeId}: ${products.length} products, avg gaps: ${(products.reduce((sum, p) => sum + (p.gaps || 0), 0) / Math.max(products.length, 1)).toFixed(1)}`;
  }).join('\n');

  const user = `Compare R.M.Williams' product catalogue across these storefronts:\n${storeData}\n\nProvide actionable insights for the global merchandising team.`;

  return callAI(system, user, 800);
}
